// src/components/UpdateBanner.jsx
import React, { useState, useEffect } from 'react';

const DISMISS_STORAGE_KEY = 'updateBanner.dismissedVersion';

function readDismissedVersion() {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    return window.sessionStorage.getItem(DISMISS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeDismissedVersion(version) {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    if (version) {
      window.sessionStorage.setItem(DISMISS_STORAGE_KEY, String(version));
    } else {
      window.sessionStorage.removeItem(DISMISS_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Banner não-intrusivo que aparece quando há uma nova versão do app disponível.
 * Permite ao usuário finalizar sua ação atual antes de atualizar, ou dispensar
 * o aviso (botão X). A dispensa é por versão: se uma versão ainda mais nova
 * aparecer depois, o banner volta a ser exibido.
 */
export function UpdateBanner({ onUpdate, newVersion }) {
  const [dismissedVersion, setDismissedVersion] = useState(readDismissedVersion);

  // Se a versão disponível mudar (ficou mais nova que a dispensada), reexibe o banner.
  useEffect(() => {
    if (newVersion && dismissedVersion && newVersion !== dismissedVersion) {
      setDismissedVersion(null);
      writeDismissedVersion(null);
    }
  }, [newVersion, dismissedVersion]);

  if (!onUpdate) return null;

  const isDismissed =
    !!dismissedVersion && (!newVersion || dismissedVersion === newVersion);
  if (isDismissed) return null;

  const handleDismiss = () => {
    const v = newVersion || 'unknown';
    setDismissedVersion(v);
    writeDismissedVersion(v);
  };

  return (
    <>
      <style>{`
        @keyframes update-banner-slide-up {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .update-banner { animation: none !important; }
        }
      `}</style>
      <div
        className="update-banner"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
          paddingLeft: 'max(16px, env(safe-area-inset-left))',
          paddingRight: 'max(16px, env(safe-area-inset-right))',
          paddingTop: '12px',
          backgroundColor: '#FFFFFF',
          color: '#1E1E1E',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          zIndex: 9999,
          boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.06)',
          fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, Inter, Roboto, sans-serif',
          animation: 'update-banner-slide-up .35s cubic-bezier(.4,0,.2,1) both',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: 600,
              lineHeight: 1.4,
              color: '#1E1E1E',
            }}
          >
            Nova versão disponível{newVersion ? ` (${newVersion})` : ''}
          </p>
          <p
            style={{
              margin: '2px 0 0',
              fontSize: '12px',
              color: '#6B7280',
              lineHeight: 1.3,
            }}
          >
            Atualize para ter a melhor experiência
          </p>
        </div>
        <button
          onClick={onUpdate}
          style={{
            flexShrink: 0,
            padding: '10px 16px',
            minHeight: '44px',
            borderRadius: '999px',
            border: 'none',
            backgroundColor: '#D32F2F',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
            transition: 'background-color 0.18s ease, transform 0.18s ease',
            whiteSpace: 'nowrap',
            WebkitTapHighlightColor: 'transparent',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#B71C1C';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = '#D32F2F';
          }}
        >
          Atualizar agora
        </button>
        <button
          onClick={handleDismiss}
          aria-label="Dispensar aviso de atualização"
          title="Dispensar"
          style={{
            flexShrink: 0,
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            borderRadius: '999px',
            border: 'none',
            backgroundColor: 'transparent',
            color: '#6B7280',
            fontSize: '20px',
            lineHeight: 1,
            cursor: 'pointer',
            transition: 'background-color 0.18s ease, color 0.18s ease',
            fontFamily: 'inherit',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
            e.currentTarget.style.color = '#1E1E1E';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#6B7280';
          }}
        >
          ×
        </button>
      </div>
    </>
  );
}

export default UpdateBanner;
